import { useMemo } from 'react';
import {
  AggregationDateFilterInput,
  DateRangeGrouping,
  InputMaybe,
  KpisQuery,
  ToursOfOrgUnitQuery,
  ToursOfOrgUnitQueryVariables,
  useToursOfOrgUnitQuery,
} from '@graphql-client/graphql';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import { useSelector } from 'react-redux';

import { selectToken } from '@redux/auth.selectors';
import { ConfigKey, getEnvVariable } from '@legacy-modules/common/services/EnvironmentConfigurationService';

let requestId = null;
export const generateRequestId = () => {
  if (requestId === null) {
    requestId = crypto.randomUUID();
  }
  return requestId;
};

type InternalParameters = {
  orgId: string;
  dateFilter: AggregationDateFilterInput;
  dateRangeGrouping?: InputMaybe<DateRangeGrouping>;
};

/** Map our internal frontend input variables to API type */
const InterfaceMapper = (internal: InternalParameters): ToursOfOrgUnitQueryVariables => ({
  orgUnit: { orgId: Number(internal.orgId) },
  dateFilter: internal.dateFilter,
  // Set explicitly to 'Day' here because we need day-based representation for tour links within UI
  dateRangeGrouping: DateRangeGrouping.Day,
});

export function useToursOfOrgUnitWithAuth<TData = ToursOfOrgUnitQuery, TError = unknown>(
  variables: InternalParameters,
  options?: UseQueryOptions<ToursOfOrgUnitQuery, TError, TData>
): UseQueryResult<TData, TError> {
  const token = useSelector(selectToken);
  const parameters = InterfaceMapper(variables);
  const dataSource = useMemo<{ endpoint: string; fetchParams?: RequestInit }>(
    () => ({
      endpoint: getEnvVariable(ConfigKey.GQL_API_URL),
      fetchParams: {
        headers: {
          Authorization: `Bearer ${token}`,
          'content-type': 'application/json',
        },
      },
    }),
    [token]
  );
  return useToursOfOrgUnitQuery<TData, TError>(dataSource, parameters, options);
}
